import {
  Box,
  ButtonBase,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import { type ReactElement, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import type { AttachmentRowModel } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/attachment-utils';
import { setConversationAttachmentDragData } from 'src/utils/conversation-attachment-drag';

const attachmentRowButtonSx: SxProps<Theme> = {
  alignItems: 'center',
  borderRadius: 1,
  justifyContent: 'space-between',
  px: 0.5,
  py: 0.75,
  textAlign: 'left',
  transition: 'background-color 0.2s ease',
  width: '100%',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
};

const attachmentRowContainerSx: SxProps<Theme> = {
  alignItems: 'center',
  borderRadius: 1,
  gap: 0.5,
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'action.hover',
    '& .conversation-attachment-row-menu': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
};

const attachmentRowContentSx: SxProps<Theme> = {
  alignItems: 'center',
  gap: 1,
  justifyContent: 'space-between',
  overflow: 'hidden',
  width: '100%',
};

const attachmentRowPrimaryGroupSx: SxProps<Theme> = {
  alignItems: 'center',
  flex: '1 1 auto',
  gap: 0.5,
  minWidth: 0,
  overflow: 'hidden',
};

const attachmentRowSecondaryGroupSx: SxProps<Theme> = {
  alignItems: 'center',
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
};

const attachmentRowNameSx: SxProps<Theme> = {
  flex: '0 1 auto',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const attachmentRowSuffixSx: SxProps<Theme> = {
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

const attachmentRowSizeSx: SxProps<Theme> = {
  ...attachmentRowSuffixSx,
  ml: 0.5,
};

const attachmentRowDateSx: SxProps<Theme> = {
  color: 'text.secondary',
  flexShrink: 0,
  fontSize: '0.6875rem',
  fontWeight: 500,
  ml: 1,
  textAlign: 'end',
  whiteSpace: 'nowrap',
};

const AttachmentRowContent = ({
  attachment,
}: {
  attachment: AttachmentRowModel;
}): ReactElement => {
  return (
    <Stack direction="row" sx={attachmentRowContentSx}>
      <Stack direction="row" sx={attachmentRowPrimaryGroupSx}>
        <Box
          component="span"
          data-icon-name={attachment.iconName}
          data-testid="conversation-attachment-icon"
          sx={{ alignItems: 'center', display: 'flex', flexShrink: 0 }}
        >
          <DynamicIcon
            name={attachment.iconName}
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              height: '0.875rem',
              width: '0.875rem',
            }}
          />
        </Box>
        <Stack direction="row" sx={attachmentRowSecondaryGroupSx}>
          <Typography
            data-testid="conversation-attachment-file-name"
            fontSize="0.75rem"
            fontWeight={500}
            sx={attachmentRowNameSx}
          >
            {attachment.fileNameBase}
          </Typography>
          {attachment.fileExtension ? (
            <Typography
              data-testid="conversation-attachment-extension"
              fontSize="0.75rem"
              fontWeight={500}
              sx={attachmentRowSuffixSx}
            >
              {attachment.fileExtension}
            </Typography>
          ) : null}
          {attachment.displaySize ? (
            <Typography
              color="text.secondary"
              data-testid="conversation-attachment-size"
              fontSize="0.625rem"
              sx={attachmentRowSizeSx}
            >
              {attachment.displaySize}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
      <Typography data-testid="conversation-attachment-date" sx={attachmentRowDateSx}>
        {attachment.displayDate}
      </Typography>
    </Stack>
  );
};

export const ConversationAttachmentRow = ({
  attachment,
  canDragToSiteFiles = true,
  onAddToSiteFiles,
}: {
  attachment: AttachmentRowModel;
  canDragToSiteFiles?: boolean;
  onAddToSiteFiles?: () => void;
}): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);
  const showMenu = Boolean(onAddToSiteFiles && canDragToSiteFiles);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddToSiteFiles = () => {
    onAddToSiteFiles?.();
    handleMenuClose();
  };

  const handleDragStart = (event: React.DragEvent<HTMLElement>) => {
    if (!canDragToSiteFiles) {
      return;
    }

    setConversationAttachmentDragData(event.dataTransfer, attachment.siteFilePayload);
  };

  const content = attachment.url ? (
    <ButtonBase
      aria-label={`Open ${attachment.fileName}`}
      component="a"
      href={attachment.url}
      rel="noreferrer"
      sx={attachmentRowButtonSx}
      target="_blank"
    >
      <AttachmentRowContent attachment={attachment} />
    </ButtonBase>
  ) : (
    <Box component="div" sx={attachmentRowButtonSx}>
      <AttachmentRowContent attachment={attachment} />
    </Box>
  );

  const menu = showMenu ? (
    <>
      <IconButton
        aria-label={`More options for ${attachment.fileName}`}
        className="conversation-attachment-row-menu"
        onClick={handleMenuOpen}
        size="small"
        sx={{
          flexShrink: 0,
          opacity: { xs: 1, sm: menuOpen ? 1 : 0 },
          pointerEvents: { xs: 'auto', sm: menuOpen ? 'auto' : 'none' },
          transition: 'opacity 0.2s ease',
        }}
      >
        <DynamicIcon name="more_vert" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} keepMounted>
        <MenuItem onClick={handleAddToSiteFiles}>
          <DynamicIcon name="bookmark" />
          Add to Site Files
        </MenuItem>
      </Menu>
    </>
  ) : null;

  return (
    <Stack
      data-testid="conversation-attachment-row"
      direction="row"
      draggable={canDragToSiteFiles}
      onDragStart={handleDragStart}
      sx={attachmentRowContainerSx}
    >
      {content}
      {menu}
    </Stack>
  );
};
